import React from 'react';

import { GridEventListener, GridRowParams } from '@mui/x-data-grid';

import { useNavigate } from 'react-router-dom';

import { reverse } from 'named-urls';

import { FULL_DATETIME_COLUMN_MIN_WIDTH, FULL_DATETIME_FORMAT, RegisterEntityModule } from 'core/constants';

import AddIcon from '@mui/icons-material/Add';

import { Button } from 'components/Button';

import { ColumnDefinition } from 'components/Grid/types';

import { useStore } from 'utils/hooks/store';

import Grid, { defaultFormatters, defaultRenderers } from 'components/Grid';

import { formatMoment } from 'utils/formatters/timezone';

import routes from 'core/routes';
import { EntityModules } from 'core/constants';

import { EntityRelatedTabProps } from 'components/Entities/types';

import CreateFormModal from 'containers/Forms/components/CreateFormModal';

import StartAuditModal from 'containers/Audits/components/StartAuditModal';

import { env } from 'config';

import { entityAuditsCardColumns } from 'components/Entities/EntityRelatedPage/RelatedAudits/utils';

import AbstractWrapper from './AbstractWrapper';
import { defaultProps } from './types';

const AuditsColumns = [
  {
    field: 'name',
    headerName: 'Name',
    nativeFilter: true,
  },
  {
    field: 'reporter',
    headerName: 'Auditor',
    renderCell: defaultRenderers.user,
    valueFormatter: defaultFormatters.user,
    valueGetter: (value: unknown) => ({ value }),
    nativeFilter: 'reporters',
  },
  {
    field: 'submitted',
    headerName: 'Submitted',
    minWidth: FULL_DATETIME_COLUMN_MIN_WIDTH,
    renderCell: ({ value }: { value: string | null }) =>
      value !== null ? formatMoment(value, FULL_DATETIME_FORMAT) : 'Not submitted',
    nativeFilter: 'submitted',
  },
] as unknown as ColumnDefinition[];

const AuditsRelatedToEntities: React.FunctionComponent<EntityRelatedTabProps> = ({ store }) => {
  const {
    routing,
    session: { user },
    formDetails: { setExtraParamsForCreatingInspection },
  } = useStore();

  const { details, entityType } = store;

  const auditsOnEntity = !!user?.auditsAsEntityEnabled;

  const [openCreateFormModal, setOpenCreateFormModal] = React.useState(false);

  const navigate = useNavigate();

  const onRowClick: GridEventListener<'rowClick'> = (form: GridRowParams): void => {
    navigate(reverse(routes.dashboard.audits.forms.details, { formId: form.row.uuid }));
  };

  const redirectToAudits = (): void => {
    routing.push(
      reverse(routes.dashboard[entityType as RegisterEntityModule].relatedAudits, { entityId: details?.uuid || '' })
    );
  };

  const handleCreateForm = (): void => {
    // Legacy carries the pre-link as an Inspection.*_uuid; the entity path passes it to draft-create.
    if (!auditsOnEntity) {
      setExtraParamsForCreatingInspection({ [entityType]: details?.uuid || '' });
    }
    setOpenCreateFormModal(true);
  };

  // Risks have no audits relation, so the block could only ever be empty. ENG-11817 adds it;
  // drop this clause when it lands.
  return (
    entityType !== EntityModules.Risks &&
    user?.licenses.workflows && (
      <>
        <AbstractWrapper
          headerTitle="Audits"
          headerAction={
            <Button variant="text" size="medium" startIcon={<AddIcon />} onClick={handleCreateForm}>
              New audit
            </Button>
          }
          gridElement={
            <Grid
              onRowClick={onRowClick}
              basicColumns={auditsOnEntity ? entityAuditsCardColumns : AuditsColumns}
              dataURL={
                auditsOnEntity
                  ? `/api/entities/${entityType}/${details?.uuid}/audits/`
                  : `${env.REACT_APP_WORKFLOWS_API_URL}inspections/?entity=${details?.uuid}`
              }
              {...defaultProps}
            />
          }
          footerAction={
            <Button variant="text" size="medium" onClick={redirectToAudits}>
              View all...
            </Button>
          }
        />
        {auditsOnEntity ? (
          <StartAuditModal
            open={openCreateFormModal}
            onClose={() => setOpenCreateFormModal(false)}
            relatedEntityUuid={details?.uuid}
          />
        ) : (
          <CreateFormModal open={openCreateFormModal} setOpen={setOpenCreateFormModal} />
        )}
      </>
    )
  );
};

export default AuditsRelatedToEntities;
